import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBuyerDetails, getBuyerConversation } from '../../../../services/buyerApis';
import ConversationDialog from '../Leads/ConversationDialog';
import { useAuth } from '../../../../context/AuthContext'; // NEW
import { useToast } from '../../../../hooks/use-toast';    // NEW
import { usePermissions } from '../../../../hooks/usePermissions'; // NEW
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Button } from "../../../../components/ui/button";
import { Separator } from "../../../../components/ui/separator";
import { Loader2, User, MapPin, Phone, Mail, ShieldCheck, ShieldAlert, Store, ArrowUpRight, Calendar, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

const BuyerDetailPage = () => {
    const { buyerId } = useParams();
    const [buyer, setBuyer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Context & Hooks for Admin Actions
    const { token } = useAuth();
    const { toast } = useToast();
    const { hasPermission } = usePermissions();

    // State for action processing and modals
    const [isProcessing, setIsProcessing] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // 'suspend', 'unsuspend', 'restore', 'delete'
    const [confirmStep, setConfirmStep] = useState(0); 

    const fetchBuyerData = useCallback(async (showLoader = true) => {
        if (showLoader) setLoading(true);
        try {
            const data = await getBuyerDetails(buyerId);
            setBuyer(data);
        } catch (err) {
            const msg = err.response?.data?.detail || "Failed to load buyer details.";
            setError(msg);
        } finally {
            if (showLoader) setLoading(false);
        }
    }, [buyerId]);

    useEffect(() => {
        fetchBuyerData();
    }, [fetchBuyerData]);

    useEffect(() => {
            if (buyer) {
                const displayName = buyer.name;
                document.title = `B | ${displayName} - Buyer Management`;
            }
            
            // Reset title when the component unmounts (leaving the page)
            return () => {
                document.title = "Buyer Management";
            };
        }, [buyer]);

    const executeAccountAction = async (actionEndpoint, successMessage) => {
        setIsProcessing(true);
        const method = actionEndpoint === 'delete' ? 'DELETE' : 'POST';
        const url = actionEndpoint === 'delete' ? '' : `/${actionEndpoint}`;
        
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/manage-user/users/${buyer.user_id}${url}`, {
                method: method,
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!response.ok) throw new Error('Action failed.');
            toast({ variant: 'success', title: successMessage });
            
            // Silently refresh the buyer data to update badges and buttons
            fetchBuyerData(false);
        } catch (error) {
            toast({ variant: 'destructive', title: `Failed to ${actionEndpoint} user.` });
        } finally {
            setIsProcessing(false);
            setPendingAction(null);
            setConfirmStep(0);
        }
    };

    const initiateAction = (action) => {
        setPendingAction(action);
        setConfirmStep(1);
    };

    if (loading) return <div className="h-96 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
    if (error) return <div className="p-6 text-red-600 bg-red-50 rounded-lg border border-red-200 m-6">{error}</div>;
    if (!buyer) return null;

    // Helper for verification badges
    const VerificationBadge = ({ isVerified, label }) => (
        <Badge variant={isVerified ? "outline" : "secondary"} className={`${isVerified ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500"} flex items-center gap-1 w-fit`}>
            {isVerified ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
            {isVerified ? `${label} Verified` : `${label} Unverified`}
        </Badge>
    );

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header Section */}
            <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                    <div className="h-16 w-16 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 text-slate-400">
                        <User className="h-8 w-8" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">{buyer.name}</h1>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                            <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">ID: {buyer.buyer_id}</span>
                            <span>•</span>
                            <span>Joined {format(new Date(buyer.created_at), 'dd MMM yyyy')}</span>
                            <span>•</span>
                            <Badge className={
                                buyer.status === 'active' ? "bg-green-600" : 
                                buyer.status === 'suspended' ? "bg-orange-600" : "bg-gray-600"
                            }>
                                {buyer.status.toUpperCase()}
                            </Badge>
                        </div>
                    </div>
                </div>

                {/* Header Actions */}
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <ConversationDialog
                        title={buyer.name}
                        fetchConversation={() => getBuyerConversation(buyer.buyer_id)}
                    />

                    {/* Seller Linkage Action */}
                    {buyer.is_seller && buyer.linked_seller_profile_id && (
                        <Button asChild className="gap-2 bg-brand-purple hover:bg-brand-purple/90">
                            <Link to={`/admin/sellers/${buyer.linked_seller_profile_id}/view`}
                                target="_blank"
                                rel="noopener noreferrer">
                                <Store className="w-4 h-4" />
                                View Seller Profile
                            </Link>
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* Column 1: Contact Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground" /> Contact & Verification
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted-foreground">Email Address</label>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm truncate pr-2">
                                    <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                                    <span className="truncate">{buyer.email || <span className="text-slate-400 italic">Not Provided</span>}</span>
                                </div>
                                {buyer.email && <VerificationBadge isVerified={buyer.is_email_verified} label="Email" />}
                            </div>
                        </div>
                        <Separator />
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted-foreground">Phone Number</label>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm truncate pr-2">
                                    <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                                    <span className="truncate">{buyer.phone || <span className="text-slate-400 italic">Not Provided</span>}</span>
                                </div>
                                {buyer.phone && <VerificationBadge isVerified={buyer.is_phone_verified} label="Phone" />}
                            </div>
                        </div>
                        <Separator />
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted-foreground">User ID</label>
                            <div className="font-mono text-sm bg-slate-50 p-1 rounded w-fit">
                                {buyer.user_id}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Column 2: Location Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" /> Location Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground block mb-1">City</label>
                                <span className="text-sm">{buyer.city || '-'}</span>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground block mb-1">State</label>
                                <span className="text-sm">{buyer.state || '-'}</span>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground block mb-1">Post Code</label>
                                <span className="text-sm font-mono">{buyer.post_code || '-'}</span>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground block mb-1">Country</label>
                                <span className="text-sm">{buyer.country || '-'}</span>
                            </div>
                        </div>
                        {buyer.last_active_at && (
                            <div className="pt-2 mt-2 border-t">
                                <label className="text-xs text-muted-foreground">Last Active</label>
                                <p className="text-sm text-slate-700">{format(new Date(buyer.last_active_at), 'PPP p')}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Column 3: Account Management (Danger Zone) */}
                <Card className={buyer.status === 'deleted' ? 'border-gray-300' : 'border-red-100'}>
                    <CardHeader>
                        <CardTitle className={`text-base flex items-center gap-2 ${buyer.status === 'deleted' ? 'text-gray-700' : 'text-red-700'}`}>
                            <AlertTriangle className="w-4 h-4" /> Account Management
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {hasPermission("users:update-buyer-status") ? (
                            <div className="flex flex-col gap-3">
                                {buyer.status === 'deleted' ? (
                                    <Button variant="outline" className="w-full justify-center text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700" onClick={() => initiateAction('restore')} disabled={isProcessing}>
                                        Restore User Account
                                    </Button>
                                ) : (
                                    <>
                                        <Button variant="destructive" className="w-full justify-center" onClick={() => initiateAction('delete')} disabled={isProcessing}>
                                            Delete User
                                        </Button>
                                    </>
                                )}
                                <p className="text-[11px] text-muted-foreground mt-1 leading-tight">
                                    {buyer.status === 'deleted'
                                        ? "This user is currently in a 30-day pending deletion state. Restoring them grants full access."
                                        : "Suspending restricts access. Deleting starts a 30-day permanent deletion timer and logs the user out globally."
                                    }
                                </p>
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500 italic">You do not have permission to manage this account.</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Leads History Table */}
            <Card>
                {/* ... existing leads table ... */}
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" /> Lead History
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {buyer.leads_history && buyer.leads_history.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[100px]">Lead ID</TableHead>
                                    <TableHead>Category</TableHead>
                                    <TableHead>Created Date</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {buyer.leads_history.map((lead) => (
                                    <TableRow key={lead.lead_id}>
                                        <TableCell className="font-mono text-xs">{lead.lead_id}</TableCell>
                                        <TableCell className="font-medium">{lead.category_name}</TableCell>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {format(new Date(lead.created_at), 'dd MMM yyyy, HH:mm')}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="uppercase text-[10px] tracking-wide">
                                                {lead.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" asChild>
                                                <Link 
                                                    to={`/admin/lead/${lead.lead_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800">
                                                    View <ArrowUpRight className="w-3 h-3" />
                                                </Link>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="py-8 text-center text-slate-500 bg-slate-50 rounded-lg border border-dashed">
                            No leads placed by this buyer yet.
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* DOUBLE CONFIRMATION MODAL */}
            {pendingAction && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-background p-6 rounded-lg shadow-lg max-w-md w-full border border-border">
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            {confirmStep === 1 ? 'Action Required' : 'Final Confirmation'}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            {confirmStep === 1 
                                ? `Are you sure you want to ${pendingAction} this buyer?` 
                                : `WARNING: Please confirm one more time. You are about to ${pendingAction} this buyer.`}
                        </p>
                        <div className="flex justify-end gap-3">
                            <Button variant="ghost" onClick={() => { setPendingAction(null); setConfirmStep(0); }}>
                                Cancel
                            </Button>
                            {confirmStep === 1 ? (
                                <Button 
                                    variant={pendingAction === 'delete' ? 'destructive' : 'default'} 
                                    onClick={() => setConfirmStep(2)}
                                >
                                    Yes, I'm sure
                                </Button>
                            ) : (
                                <Button 
                                    variant={pendingAction === 'delete' ? 'destructive' : 'default'} 
                                    onClick={() => executeAccountAction(pendingAction, `User ${pendingAction}ed successfully`)}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? 'Processing...' : `Absolutely ${pendingAction}`}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BuyerDetailPage;